<template>
  <div id="myModal" class="modal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>
          <slot name="title" />
          <button id="modal-close-icon" data-qa-id="modal-close-icon" class="modal-close-button" @click="closeModal"
            :disabled="getLoading || isBundlePublishing || isBundleDiscarding || savingDraft">
            <Close />
          </button>
        </h2>
      </div>
      <div class="modal-body">
        <p>
          <slot name="content" />
        </p>
      </div>
      <div class="modal-footer">
        <slot name="actions" />
        <button v-if="showButtons === 'confirmandcancel' || showButtons === 'cancelandunsubscribe'" id="modal-cancel-button"
          class="modal-button modal-button--cancel" data-qa-id="modal-cancel-button" :disabled="getLoading || isBundlePublishing || isBundleDiscarding || savingDraft"
          @click="closeModal">
          {{ $t('buttons.cancel') }}
        </button>
        <button
          v-if="showButtons === 'confirmandcancel'" id="modal-confirm-button"
          class="modal-button modal-button--confirm" :class="{ 'modal-button--confirm--cooldownDisabled': disableConfirmButton }" data-qa-id="modal-confirm-button" :disabled="getLoading || isBundlePublishing || isBundleDiscarding || savingDraft || disableConfirmButton"
          @click="confirmModal"
        >
          <animation-processing
            v-if="getLoading || isBundlePublishing || isBundleDiscarding || savingDraft" id="modal-confirm-loading" fill="new-white"
            class=" modal-button--confirm--pageSpinner" data-qa-id="modal-confirm-loading" height="48px" width="48px" />
          <span v-else> {{ confirmButtonLabel || $t('buttons.confirm') }}</span>
        </button>
        <button v-if="showButtons === 'okay'" id="modal-okay-button" class="modal-button modal-button--confirm"
          data-qa-id="modal-okay-button" :disabled="getLoading" @click="closeModal">
          {{ $t('buttons.okay') }}
        </button>
        <button v-if="showButtons === 'soundsgood'" id="modal-okay-button" class="modal-button modal-button--confirm"
          data-qa-id="modal-sounds-good-button" :disabled="getLoading" @click="closeModal">
          {{$t('pages.sales_report.download_modal.footer_btn_text')}}
        </button>
        <button v-if="showButtons === 'cancelandunsubscribe'" id="modal-unsubscribe-button" class="modal-button modal-button--unsubscribe"
          data-qa-id="modal-sounds-good-button" @click="unsubscribeModal">
          {{$t('buttons.unsubscribe')}}
        </button>
        <a v-if="showButtons === 'contact'" :href="emailDataValue"
          class="modal-button modal-button--confirm"
          data-qa-id="modal-contact-button" @click="closeModal">
          {{ $t('buttons.contact') }}
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import Close from 'src/components/icons/close/Close.vue'
import AnimationProcessing from 'components/animations/processing'
import { mapGetters } from 'vuex'
import { disableScrollOnModalOpen } from 'src/utils/helper/misc'
import DOMPurify from 'dompurify'

export default {
  components: {
    Close,
    AnimationProcessing
  },

  props: {
    showButtons: {
      type: String,
      default: ''
    },
    isBundlePublishing: {
      type: Boolean,
      default: false
    },
    isBundleDiscarding: {
      type: Boolean,
      default: false
    },
    savingDraft: {
      type: Boolean,
      default: false
    },
    emailData: {
      type: String,
      default: '',
    },
    confirmButtonLabel: {
      type: String,
      default: ''
    },
    disableConfirmButton: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    disableScrollOnModalOpen(true)
  },

  beforeUnmount() {
    disableScrollOnModalOpen(false)
  },

  methods: {
    closeModal() {
      disableScrollOnModalOpen(false)
      this.$emit('update:bundlepopup')
    },

    confirmModal() {
      disableScrollOnModalOpen(false)
      this.$emit('update:bundlepopupconfirm')
    },

    unsubscribeModal() {
      disableScrollOnModalOpen(false)
      this.$emit('update:popupunsubscribe')
    },
  },

  computed: {
    emailDataValue() {
      return DOMPurify.sanitize(this.emailData)
    },
    ...mapGetters('bundle', ['getLoading'])
  }
}
</script>
<style lang="scss" scoped>
@import 'mpl-css/lib/assets/global/variables/fonts';
@import 'mpl-css/lib/assets/global/functions/index';
@import 'mpl-css/lib/assets/global/mfpfonts/fonts.scss';
@import 'mpl-css/lib/assets/global/variables/colors.scss';
@import 'mpl-css/lib/assets/global/variables/breakpoints';

.modal {
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.24);

  .modal-content {
    background-color: map-get($colors,'white');
    border: 1px solid #ccc;
    max-width: rem(464);
    max-height: rem(464);
    width: 60%;
    padding: rem(40);
    position: relative;
    border-radius: 16px;
    margin: 0;
    color: #1E242C;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media screen and (max-width: 719px) {
      min-width: 85%;
      padding: 2rem;
    }

    .modal-header {
      &>h2 {
        color: map-get($colors, 'spirits-900');
        @extend .mfp-HelveticaNowMTText-Medium-28;
        line-height: rem(32);

        @media screen and (max-width: 719px) {
          letter-spacing: rem(-0.26);
          font-size: 23px;
        }
      }

      .modal-close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        height: 1.5rem;
        width: 1.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        color: map-get($colors, 'spirits-600');

        &:hover {
          cursor: pointer;
          background: map-get($colors, 'spirits-200');
          border-radius: 64px;

          svg {
            filter: brightness(0);
          }
        }
      }
    }

    .modal-body {
      @extend .mfp-HelveticaNowMTText-Regular-16;
      color: map-get($colors, 'spirits-700');
      line-height: rem(24);
      @media screen and (max-width: 719px) {
        letter-spacing: unset;
        font-size: 13px;
      }
    }

    .modal-footer {
      text-align: right;
      display: flex;
      gap: 1rem;
      align-items: center;

      .modal-button {
        @extend .mfp-HelveticaNowMTText-Medium-16;
        border-radius: 8px;
        letter-spacing: rem(-0.47);
        width: fit-content;
        height: rem(48);
        padding: rem(12) rem(16);
        display: flex;
        justify-content: center;
        align-items: center;

        @media screen and (max-width: 719px) {
          font-size: 13px;
        }

        &--cancel {
          cursor: pointer;
          background: map-get($colors, 'white');
          color: map-get($colors, 'spirits--900');
          border: 1px solid map-get($colors, 'spirits-300');

          &:hover {
            background:  map-get($colors, 'spirits-300');
          }
        }

        &--confirm {
          cursor: pointer;
          background: map-get($colors, 'blueduck-500');
          border: 1px solid map-get($colors, 'blueduck-500');
          color: map-get($colors, 'white');

          &--cooldownDisabled {
            opacity: 0.4;
          }

          &--pageSpinner {
            width: rem(20);
            height: rem(20);
            margin-bottom: 0;
          }

          &:hover {
            background: map-get($colors, 'blueduck-600');
          }
        }

        &--okay {
          cursor: pointer;
          background: map-get($colors, 'spirits-200');
          border: 1px solid map-get($colors, 'spirits-200');
          color: #1E242C;
          &:hover {
            background: map-get($colors, 'spirits-300');
          }

          @media screen and (max-width: map-get($breakpoints, 'tab')) {
            display: none;
          }
        }

        &--unsubscribe {
          cursor: pointer;
          background: map-get($colors, 'red-babe--500');
          color: map-get($colors, 'white');
        }
      }
    }
  }
}
</style>
